import { useEffect, useMemo, useState } from "react";
import { DefaultValues, FieldValues, SubmitHandler, useForm, UseFormReturn } from "react-hook-form";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { useErrorBoundary } from "react-error-boundary";
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from "@shared/lib/utils";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { DEFAULT_BROWSE_PER_PAGE, PaginatedMeta } from "@shared/api/pagination";
import { useEntityBrowse } from "./useEntityBrowse";
import { DEFAULT_SORT_KEY, ParsedBrowseUrl } from "../utils/browseUrlParams";
import { useBrowsePagination } from "./useBrowsePagination";
import { isBrowseCacheEligible } from "./useBrowseCacheEligible";

type QueryResult<TData> = {
  data?: {
    data: TData[];
    links: {
      first: string | null;
      last: string | null;
      prev?: string | null;
      next?: string | null;
    };
    meta: PaginatedMeta;
  };
  isFetching: boolean;
  error?: unknown;
};

type UseResourceBrowseOptions<TSearch extends FieldValues, TData, TSort extends string> = {
  parseBrowseUrlParams: (searchParams: URLSearchParams) => ParsedBrowseUrl<TSearch>;
  syncBrowseUrlParams: (
    setSearchParams: SetURLSearchParams,
    data: TSearch,
    sortKey: string,
    sortMethod: "ASC" | "DESC",
  ) => void;
  buildSort: (sortKey: string, sortMethod: "ASC" | "DESC") => TSort;
  useBrowseQuery: (args: { filter: Partial<TSearch>; sort: TSort[]; per_page: number; cursor?: string; useCache?: boolean }) => QueryResult<TData>;
  defaultPerPage?: number;
};

type ResourceBrowseResult<TSearch extends FieldValues, TData> = {
  form: UseFormReturn<TSearch>;
  data: TData[];
  isLoading: boolean;
  sortKey: string;
  sortMethod: "ASC" | "DESC";
  handleSortKeyChanged: (value: string) => void;
  handleSortMethodChanged: (value: string) => void;
  page: number;
  perPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  handleNextPage: () => void;
  handlePrevPage: () => void;
  handleRowsPerPageChange: (perPage: number) => void;
  reset: () => void;
};

export function useResourceBrowse<TSearch extends FieldValues, TData, TSort extends string>({
  parseBrowseUrlParams,
  syncBrowseUrlParams,
  buildSort,
  useBrowseQuery,
  defaultPerPage = DEFAULT_BROWSE_PER_PAGE,
}: UseResourceBrowseOptions<TSearch, TData, TSort>): ResourceBrowseResult<TSearch, TData> {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBoundary } = useErrorBoundary();

  const urlParams = useMemo(() => parseBrowseUrlParams(searchParams), [parseBrowseUrlParams, searchParams]);
  const [sortKey, setSortKey] = useState(urlParams.sortKey || DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(urlParams.sortMethod);
  const [queryFilter, setQueryFilter] = useState<Partial<TSearch>>(urlParams.filters ?? {});
  const [data, setData] = useState<TData[]>([]);
  const pagination = useBrowsePagination(defaultPerPage);

  const form = useForm<TSearch>({
    defaultValues: (urlParams.filters ?? {}) as DefaultValues<TSearch>,
  });

  const {
    data: queryData,
    isFetching: isLoading,
    error,
  } = useBrowseQuery({
    filter: queryFilter,
    sort: [buildSort(sortKey, sortMethod)],
    per_page: pagination.perPage,
    cursor: pagination.cursor,
    useCache: isBrowseCacheEligible({
      filters: queryFilter,
      sortKey,
      sortMethod,
      cursor: pagination.cursor,
    }),
  });

  useEffect(() => {
    if (queryData) {
      setData(queryData.data);
    }
  }, [queryData]);

  useEffect(() => {
    if (error) {
      handleErrorWithBoundary(error, showBoundary);
    }
  }, [error, showBoundary]);

  const onSearch: SubmitHandler<TSearch> = (formData) => {
    pagination.resetPagination();
    syncBrowseUrlParams(setSearchParams, formData, sortKey, sortMethod);
    setQueryFilter(deleteEmptyFieldsFromObject(formData));
  };

  useEntityBrowse({
    form,
    sortKey,
    sortMethod,
    onSearch,
  });

  function handleSortKeyChanged(value: string) {
    pagination.resetPagination();
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    pagination.resetPagination();
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function handleNextPage() {
    pagination.handleNextPage(queryData?.meta.next_cursor);
  }

  function handlePrevPage() {
    pagination.handlePrevPage(queryData?.meta.prev_cursor);
  }

  function reset() {
    const values = setAllValuesToNull(form.getValues());
    form.reset(values as DefaultValues<TSearch>);
  }

  return {
    form,
    data,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    page: pagination.page,
    perPage: pagination.perPage,
    hasNextPage: Boolean(queryData?.meta.next_cursor),
    hasPrevPage: Boolean(queryData?.meta.prev_cursor),
    handleNextPage,
    handlePrevPage,
    handleRowsPerPageChange: pagination.handleRowsPerPageChange,
    reset,
  };
}
